import React, { FC, useState, useEffect, useCallback } from 'react';
import { SwiperSlide } from 'swiper/react';

import { useGetFeaturedSparkCards } from 'graphQL/sparks/hooks';
import SliderInteractive from 'components/SliderInteractive';
import SparkSelectionModal from 'components/Profile/SparksList/SparkSelectionModal';
import { COLORS } from 'styles/constants';
import useTheme from 'helpers/useTheme';

import FeaturedSparkCard from './FeaturedSparkCard';
import { StyledFeaturedSparksSection, StyledHeader, StyledTitle, StyledSubtitle, StyledSliderWrap } from './styled';

const FeaturedSparksSection: FC = () => {
  const isDark = useTheme();
  const { items, loading, loadMore } = useGetFeaturedSparkCards({ limit: 10 });
  const [slideIndex, setSlideIndex] = useState(0);
  const [isSparkModalOpen, setIsSparkModalOpen] = useState(false);
  const [selectedCategorySlug, setSelectedCategorySlug] = useState<string | undefined>(undefined);

  // Load more when approaching the end of the carousel
  useEffect(() => {
    if (items.length && slideIndex >= items.length - 3) {
      loadMore();
    }
  }, [slideIndex, items.length, loadMore]);

  const handleOpenSparkModal = useCallback((categorySlug?: string) => {
    setSelectedCategorySlug(categorySlug);
    setIsSparkModalOpen(true);
  }, []);

  const handleCloseSparkModal = useCallback(() => {
    setIsSparkModalOpen(false);
    setSelectedCategorySlug(undefined);
  }, []);

  // Don't render if no featured sparks
  if (!loading && !items.length) {
    return null;
  }

  return (
    <StyledFeaturedSparksSection>
      <StyledHeader>
        <StyledTitle>Popular Sparks</StyledTitle>
        <StyledSubtitle>Curated questions set the theme</StyledSubtitle>
      </StyledHeader>

      {items.length > 0 && (
        <StyledSliderWrap>
          <SliderInteractive
            onSlideChange={(ind) => setSlideIndex(ind)}
            customSlidesPerView="auto"
            spaceBetween={12}
            offset={12}
            fontColor={isDark ? COLORS.white[100] : COLORS.brown.dark[100]}
            showBtnsSm={!!items.length}
            isInfinite={false}
            isPrevDisabled={slideIndex === 0}
            isNextDisabled={slideIndex >= items.length - 2}
          >
            {items.map((spark) => (
              <SwiperSlide key={spark.id}>
                <FeaturedSparkCard card={spark} onSparkButtonClick={handleOpenSparkModal} />
              </SwiperSlide>
            ))}
          </SliderInteractive>
        </StyledSliderWrap>
      )}

      <SparkSelectionModal
        isOpen={isSparkModalOpen}
        onClose={handleCloseSparkModal}
        initialCategorySlug={selectedCategorySlug}
      />
    </StyledFeaturedSparksSection>
  );
};

export default FeaturedSparksSection;
